import { useState } from 'react';
import { Modal } from '@react-apps/design-system';
import type { Product } from '../../domain/entities/catalog.entity';
import { useProductPerks } from '../hooks/use-product-perks';
import { CreateProductModal } from './CreateProductModal';
import { ProductPerksEditorModal } from './ProductPerksEditorModal';

interface CreateProductFromSourceModalProps {
  /** Produto clicado em "Criar novo baseado neste". `null` fecha o fluxo. */
  source: Product | null;
  onClose: () => void;
}

/**
 * Entrega 2 do editor de associação (§8 do plano): criar produto a partir de
 * outro do mesmo tipo. Duas etapas sequenciais — nome/ID externo primeiro
 * (produto nasce vazio, como sempre), depois o editor de perks já aberto,
 * pré-populado com o que a origem tem. Nada é gravado antes do "Aplicar
 * alterações" dessa segunda etapa.
 */
export function CreateProductFromSourceModal({
  source,
  onClose,
}: CreateProductFromSourceModalProps) {
  const [createdProduct, setCreatedProduct] = useState<Product | null>(null);
  const { data: sourcePerks = [], isLoading: sourcePerksLoading } =
    useProductPerks(source?.id ?? '');

  const close = () => {
    setCreatedProduct(null);
    onClose();
  };

  if (!source) return null;

  if (!createdProduct) {
    return (
      <CreateProductModal
        open
        onClose={close}
        onCreated={setCreatedProduct}
        modalDescription={`Baseado em "${source.name}". O produto nasce vazio; a próxima etapa traz os perks de lá para revisão antes de confirmar.`}
      />
    );
  }

  if (sourcePerksLoading) {
    return (
      <Modal open onClose={close} title="Carregando perks de origem…">
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      </Modal>
    );
  }

  return (
    <ProductPerksEditorModal
      productId={createdProduct.id}
      productName={createdProduct.name}
      open
      onClose={close}
      initialAdditions={sourcePerks}
    />
  );
}
